import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { ComponentSpinner } from "../spinner";
import PropTypes from "prop-types";
import classnames from "classnames";
import PaginationBar from "./pagination-bar";
import { NoDataComponent } from "../no-data-component";

// ─── Pagination bar ────────────────────────────────────────────────────────────

const DEFAULT_ROWS_OPTIONS = [10, 25, 50, 100];

// ─── Sort icon ─────────────────────────────────────────────────────────────────

const SortIcon = ({ direction }) => {
  if (direction === "asc") return <span className="ml-1 opacity-100">▲</span>;
  if (direction === "desc") return <span className="ml-1 opacity-100">▼</span>;
  return <span className="ml-1 opacity-40">⇅</span>;
};

SortIcon.propTypes = {
  direction: PropTypes.oneOf(["asc", "desc", false])
};

// ─── Datatable ─────────────────────────────────────────────────────────────────

/**
 * Datatable powered by @tanstack/react-table v8.
 *
 * Column definition shape:
 *   { Header, accessor, sortable?, Cell?: ({ value, row }) => ReactNode }
 *
 * Pass `pagination` for server-side paging; omit for client-side paging.
 */
const Datatable = ({
  columns = [],
  data = [],
  loading = false,
  defaultLimit = 10,
  pagination, // server-side: { count, page, limit }
  defaultSortFieldId,
  handlePageChange,
  handlePerRowsChange,
  handleColumnOrderChange,
  fixed = false,
  dense = false,
  getStartedComp,
  customNoData,
  className,
  conditionalRowStyles,
  expandableRows = false,
  expandableRowsComponent,
  paginationRowsPerPageOptions = DEFAULT_ROWS_OPTIONS,
  showPagination = true,
  onRowClicked,
  footer
}) => {
  const isServerSide = Boolean(pagination);
  const isFirstSort = useRef(true);

  // ── sorting state ──────────────────────────────────────────────────────────
  const [sorting, setSorting] = useState(
    defaultSortFieldId ? [{ id: defaultSortFieldId, desc: false }] : []
  );
  // ── pagination state (client-side only) ───────────────────────────────────
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(defaultLimit);

  // ── expanded rows state ───────────────────────────────────────────────────
  const [expanded, setExpanded] = useState({});

  // ── column order state ────────────────────────────────────────────────────
  const baseColumnIds = useMemo(
    () => [
      ...(expandableRows ? ["__expander"] : []),
      ...columns.map((c) => c.accessor)
    ],
    [columns, expandableRows]
  );
  const [columnOrder, setColumnOrder] = useState(baseColumnIds);

  // Build TanStack column defs from our simple { Header, accessor, Cell, sortable } shape
  const tanstackColumns = useMemo(() => {
    const dataCols = columns.map((col) => ({
      id           : col.accessor,
      accessorKey  : col.accessor,
      header       : col.Header,
      enableSorting: col.sortable ?? false,
      cell         : col.Cell
        ? ({ getValue, row }) => col.Cell({ value: getValue(), row: row.original })
        : undefined
    }));

    if (!expandableRows) return dataCols;

    const expanderCol = {
      id    : "__expander",
      header: "",
      cell  : ({ row }) => (
        <button
          className="p-0 text-sm text-primary hover:underline"
          onClick={() => row.toggleExpanded()}
          aria-label={row.getIsExpanded() ? "Collapse row" : "Expand row"}
        >
          {row.getIsExpanded() ? "▾" : "▸"}
        </button>
      )
    };
    return [expanderCol, ...dataCols];
  }, [columns, expandableRows]);

  const table = useReactTable({
    data,
    columns: tanstackColumns,
    state  : {
      sorting,
      expanded,
      columnOrder,
      ...(isServerSide ? {} : { pagination: { pageIndex, pageSize } })
    },
    manualPagination: isServerSide,
    manualSorting   : false,
    pageCount       : isServerSide
      ? Math.ceil((pagination?.count ?? 0) / (pagination?.limit ?? pageSize))
      : undefined,
    onSortingChange    : setSorting,
    onExpandedChange   : setExpanded,
    onColumnOrderChange: (newOrder) => {
      const resolved =
        typeof newOrder === "function" ? newOrder(columnOrder) : newOrder;
      setColumnOrder(resolved);
      if (handleColumnOrderChange) {
        // Map back to the original column objects in the new order
        const reordered = resolved
          .filter((id) => id !== "__expander")
          .map((id) => columns.find((c) => c.accessor === id))
          .filter(Boolean);
        handleColumnOrderChange(reordered);
      }
    },
    onPaginationChange: isServerSide
      ? undefined
      : (updater) => {
          const next =
            typeof updater === "function"
              ? updater({ pageIndex, pageSize })
              : updater;
          setPageIndex(next.pageIndex);
          setPageSize(next.pageSize);
        },
    getCoreRowModel    : getCoreRowModel(),
    getSortedRowModel  : getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    ...(isServerSide ? {} : { getPaginationRowModel: getPaginationRowModel() })
  });

  // Notify parent of sort changes when server-side. Skip the mount firing —
  // the parent's own hook already issues the initial fetch, so re-firing here
  // on mount would double every request.
  useEffect(() => {
    if (!isServerSide) return;
    if (isFirstSort.current) {
      isFirstSort.current = false;
      return;
    }
    if (handlePageChange) {
      handlePageChange(pagination?.limit ?? pageSize, pagination?.page ?? 1);
    }
  }, [sorting]); // eslint-disable-line

  // ── resolve effective page info ────────────────────────────────────────────
  const effectivePageIndex = isServerSide
    ? (pagination?.page ?? 1) - 1
    : pageIndex;
  const effectivePageSize = isServerSide
    ? (pagination?.limit ?? defaultLimit)
    : pageSize;
  const effectiveTotal = isServerSide ? (pagination?.count ?? 0) : data.length;

  const handleClientPageChange = (newPage) => {
    setPageIndex(newPage);
  };

  const handleClientRowsChange = (newSize) => {
    setPageSize(newSize);
    setPageIndex(0);
  };

  const handleServerPageChange = (newPage) => {
    if (handlePageChange) {
      handlePageChange(effectivePageSize, newPage + 1);
    }
  };

  const handleServerRowsChange = (newSize) => {
    if (handlePerRowsChange) {
      handlePerRowsChange(newSize, 1);
    }
  };

  const rows = table.getRowModel().rows;
  const isEmpty = !loading && rows.length === 0;

  return (
    <div
      className={classnames("relative w-full overflow-hidden", className, {
        "[&_td:first-child]:sticky [&_td:first-child]:left-0 [&_td:first-child]:z-[2] [&_td:first-child]:bg-white [&_th:first-child]:sticky [&_th:first-child]:left-0 [&_th:first-child]:z-[3] [&_th:first-child]:bg-gray-50":
          fixed
      })}
    >
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
          <ComponentSpinner />
        </div>
      )}

      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sortDir = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      className={classnames(
                        "select-none whitespace-nowrap border-b border-gray-200 bg-gray-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide",
                        dense ? "py-1" : "py-2",
                        { "cursor-pointer hover:bg-gray-100": canSort }
                      )}
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {canSort && <SortIcon direction={sortDir} />}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {rows.map((row) => {
              const customStyle = conditionalRowStyles
                ? conditionalRowStyles
                    .filter((s) => s.when && s.when(row.original))
                    .reduce((acc, s) => ({ ...acc, ...s.style }), {})
                : {};
              const colSpan = row.getVisibleCells().length;
              return (
                <React.Fragment key={row.id}>
                  <tr
                    style={customStyle}
                    onClick={onRowClicked ? () => onRowClicked(row.original) : undefined}
                    className={classnames("transition-colors hover:bg-gray-50", {
                      "cursor-pointer": onRowClicked
                    })}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={classnames(
                          "whitespace-nowrap border-b border-gray-100 px-3 align-middle",
                          dense ? "py-1" : "py-2"
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                  {expandableRows &&
                    row.getIsExpanded() &&
                    expandableRowsComponent && (
                      <tr className="bg-gray-50">
                        <td
                          colSpan={colSpan}
                          className="border-b-2 border-gray-200 px-5 py-3"
                        >
                          {expandableRowsComponent({ data: row.original })}
                        </td>
                      </tr>
                    )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {isEmpty && (
        <div className="py-8 text-center text-gray-400">
          {getStartedComp ?? customNoData ?? (
            <NoDataComponent text="No records found." />
          )}
        </div>
      )}

      {footer && <div>{footer}</div>}

      {showPagination && !isEmpty && (
        <PaginationBar
          pageIndex={effectivePageIndex}
          pageSize={effectivePageSize}
          total={effectiveTotal}
          rowsPerPageOptions={paginationRowsPerPageOptions}
          onPageChange={
            isServerSide ? handleServerPageChange : handleClientPageChange
          }
          onRowsPerPageChange={
            isServerSide ? handleServerRowsChange : handleClientRowsChange
          }
        />
      )}
    </div>
  );
};

Datatable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
        .isRequired,
      accessor: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      Cell    : PropTypes.func
    })
  ).isRequired,
  data        : PropTypes.array.isRequired,
  loading     : PropTypes.bool,
  defaultLimit: PropTypes.number,
  /** Server-side pagination info: { count, page, limit } */
  pagination  : PropTypes.shape({
    count: PropTypes.number,
    page : PropTypes.number,
    limit: PropTypes.number
  }),
  defaultSortFieldId     : PropTypes.string,
  handlePerRowsChange    : PropTypes.func,
  handlePageChange       : PropTypes.func,
  handleColumnOrderChange: PropTypes.func,
  fixed                  : PropTypes.bool,
  dense                  : PropTypes.bool,
  getStartedComp         : PropTypes.node,
  customNoData           : PropTypes.node,
  className              : PropTypes.string,
  conditionalRowStyles   : PropTypes.arrayOf(
    PropTypes.shape({ when: PropTypes.func, style: PropTypes.object })
  ),
  expandableRows              : PropTypes.bool,
  expandableRowsComponent     : PropTypes.func,
  paginationRowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  showPagination              : PropTypes.bool,
  /** Called with the row's data when a row is clicked. Buttons inside a cell
   *  should stopPropagation so they don't also trigger it. */
  onRowClicked                : PropTypes.func,
  footer                      : PropTypes.any,
  module                      : PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  )
};

export default Datatable;
